/**
 * PUBLIC share landing — `/r/:code`.
 *
 * The target of every shared link. Deliberately the most permissive page in
 * the app: no auth, no ProtectedRoute, no tenant loader. A recipient who has
 * never heard of Cardmoji lands here from WhatsApp and sees the card graphic
 * plus one CTA into the funnel.
 *
 * PRIVACY: reads `card_shares` ONLY. That row carries image, colour, style and
 * orientation — no order, no name, no address, no load amount, no PAN. The
 * recipient still cannot read the sender's order (RLS), which is correct.
 *
 * LEGAL GATES (L1): no scheme name/mark, no patent claim, no present-tense
 * issuance claim.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DesignedCardFace } from "@/components/consumer/DesignedCardFace";
import { DEFAULT_DESIGNED_CARD } from "@/lib/designed-card";
import { getPublicCardShare, type PublicCardShare } from "@/services/share";
// REF CAPTURE: arriving on /r/:code is the attribution event. Persist the code
// into the funnel's first-touch attribution store so it survives the walk to
// /create and lands on the guest session.
import { readAttribution, saveAttribution } from "@/lib/consumer-session";
import { LAUNCH_PROGRAM_SLUG } from "@/services/programs";


const TITLE = "Someone made their card on Cardmoji";
const DESCRIPTION = "See the card, then design your own in a couple of minutes.";

/**
 * Set head tags client-side — for a human recipient and JS-executing crawlers.
 * Non-JS scrapers (WhatsApp, iMessage, Slack) never see these; they get the
 * site-level og:* from index.html. The `share-page` function that used to serve
 * per-card tags in the raw bytes is unusable — see `src/services/share.ts` for
 * why — so per-card unfurl waits on SSR.
 */

function useShareHead(code: string, avatarUrl: string | null) {
  useEffect(() => {
    const prev = document.title;
    document.title = TITLE;
    const set = (attr: "name" | "property", key: string, value: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    set("name", "description", DESCRIPTION);
    set("property", "og:title", TITLE);
    set("property", "og:description", DESCRIPTION);
    set("property", "og:type", "website");
    set("property", "og:url", `${window.location.origin}/r/${code}`);
    set("name", "twitter:card", "summary_large_image");
    // Same 1200x630 PNG the scraper-facing `share-page` function advertises, so
    // JS-executing crawlers and non-JS scrapers agree on one image.
    const base = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    if (base && code) {
      set("property", "og:image", `${base}/functions/v1/share-og/${code}`);
      set("name", "twitter:image", `${base}/functions/v1/share-og/${code}`);
    } else if (avatarUrl) {
      set("property", "og:image", avatarUrl);
    }
    return () => {
      document.title = prev;
    };
  }, [code, avatarUrl]);
}

export default function SharedCard() {
  const { code = "" } = useParams();
  const [share, setShare] = useState<PublicCardShare | null>(null);
  const [state, setState] = useState<"loading" | "found" | "missing">("loading");

  useShareHead(code, share?.avatarUrl ?? null);

  /**
   * Attribution on arrival — first-touch wins, so a visitor who already carries
   * a ref keeps it. The value stored is the SHARER'S REAL REFERRAL CODE off the
   * published share row, resolved server-side; the `:code` in the URL is only a
   * lookup id for the graphic and never resolved to anybody. A share published
   * before its sender had a code carries none, and the visitor arrives
   * unattributed rather than carrying a code that cannot resolve.
   */
  useEffect(() => {
    const ref = share?.referralCode;
    if (!ref) return;
    const existing = readAttribution(LAUNCH_PROGRAM_SLUG);
    if (existing.ref) return;
    saveAttribution(LAUNCH_PROGRAM_SLUG, {
      ref: ref.slice(0, 128),
      utm_source: "share_link",
      utm_medium: "referral",
      landed_at: new Date().toISOString(),
    });
  }, [share?.referralCode]);


  useEffect(() => {
    let live = true;
    (async () => {
      const row = code ? await getPublicCardShare(code) : null;
      if (!live) return;
      setShare(row);
      setState(row ? "found" : "missing");
    })();
    return () => {
      live = false;
    };
  }, [code]);

  const title = TITLE;
  const description = DESCRIPTION;

  return (
    <main className="flex min-h-[100dvh] flex-col items-center justify-center bg-paper px-6 py-12 text-ink">


      <Link
        to="/"
        className="mb-8 font-brand text-[22px] font-semibold tracking-[-0.01em] text-ink"
        data-testid="share-wordmark"
      >
        cardmoji
      </Link>

      {state === "loading" ? (
        <div
          className="h-[380px] w-[240px] animate-pulse rounded-[18px] bg-foreground/[0.08]"
          data-testid="share-loading"
        />
      ) : null}

      {state === "found" && share ? (
        <div className="flex flex-col items-center" data-testid="share-card">
          <DesignedCardFace
            card={{
              ...DEFAULT_DESIGNED_CARD,
              avatarUrl: share.avatarUrl,
              colour: share.colour,
              orientation: share.orientation,
            }}
            alt="A personalised Cardmoji card design"
            className="w-[min(280px,80vw)] shadow-[0_18px_44px_rgba(28,32,48,0.22)]"
          />
          <h1 className="mt-9 max-w-[18ch] text-center font-brand text-[32px] font-semibold leading-[1.1] tracking-[-0.02em]">
            {title}
          </h1>
        </div>
      ) : null}

      {state === "missing" ? (
        <div className="flex flex-col items-center" data-testid="share-missing">
          <h1 className="max-w-[20ch] text-center font-brand text-[32px] font-semibold leading-[1.1] tracking-[-0.02em]">
            This card link has expired
          </h1>
          <p className="mt-3 max-w-[38ch] text-center text-[15px] leading-[1.5] text-foreground/60">
            We couldn&apos;t find a card for this link — but you can still make your own.
          </p>
        </div>
      ) : null}

      {state !== "loading" ? (
        <>
          <p className="mt-3 max-w-[38ch] text-center text-[15px] leading-[1.5] text-foreground/65">
            {description}
          </p>
          <Link
            to="/create"
            className="mt-7 flex min-h-12 items-center rounded-full bg-grape px-7 text-[15px] font-semibold text-paper transition-colors hover:bg-ink"
            data-testid="share-cta"
          >
            Make your own at Cardmoji
          </Link>
          <Link
            to="/"
            className="mt-4 text-[14px] font-semibold text-foreground/60 hover:text-ink"
            data-testid="share-home"
          >
            What is Cardmoji?
          </Link>
        </>
      ) : null}
    </main>
  );
}
